/**
 * asset_form_controller.js — the add-asset form (Stimulus).
 *
 * One job: when a technician picks an asset type, render that type's
 * asset-scope field definitions into the form. Those fields are not known until
 * a type is chosen, so they cannot be server-rendered with the rest of it.
 *
 * The definitions ride on each <option> as data-definitions (already validated
 * and serialized server-side), so choosing a type costs no round trip. That
 * matters on a phone in a plant room with two bars of signal.
 *
 * NOTE the input names: `attributes[<key>]`. application/web/views/assets.py
 * `_values_from_form` unpacks that prefix back into a dict, and
 * asset_fields.validate_values does the coercion. This controller enforces
 * nothing beyond `required` — the server is the only validator that counts.
 *
 * Serial capture goes through window.ChippieBridge.scan(). The barcode bridge
 * COMPONENT is already mounted once, globally, on #bridge-host — a second
 * data-controller="bridge--barcode-scanner" here would re-register the facade,
 * so this calls the facade directly. The scan button stays hidden outside the
 * native shell, where scan() answers 'unsupported' and manual entry is the
 * whole story.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["type", "typeFields", "serial", "scanButton"];

    connect() {
        if (this.hasTypeTarget && this.typeTarget.value) this.typeChanged();
        this._revealScanButton();
    }

    _revealScanButton() {
        if (!this.hasScanButtonTarget) return;
        const bridge = window.ChippieBridge;
        const native = bridge && bridge._platform && bridge._platform() !== 'pwa';
        if (native && typeof bridge.scan === 'function') {
            this.scanButtonTarget.classList.remove('d-none');
        }
    }

    /** Scan a serial plate in the native shell. */
    scanSerial(event) {
        if (event) event.preventDefault();
        if (!window.ChippieBridge || typeof window.ChippieBridge.scan !== 'function') return;

        window.ChippieBridge.scan((err, result) => {
            if (err || !result) return;  // errored or cancelled — leave manual entry alone
            const value = result.value || result.barcode || result;
            if (typeof value === 'string' && this.hasSerialTarget) {
                this.serialTarget.value = value;
            }
        });
    }

    disconnect() {
        if (this.hasTypeFieldsTarget) this.typeFieldsTarget.innerHTML = '';
    }

    typeChanged() {
        if (!this.hasTypeFieldsTarget) return;
        this.typeFieldsTarget.innerHTML = '';

        const definitions = this._selectedDefinitions();
        definitions
            .filter((d) => d.scope === 'asset' && !d.retired)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .forEach((definition) => {
                this.typeFieldsTarget.appendChild(this._field(definition));
            });
    }

    _selectedDefinitions() {
        const option = this.typeTarget.selectedOptions[0];
        if (!option || !option.dataset.definitions) return [];
        try {
            return JSON.parse(option.dataset.definitions) || [];
        } catch (e) {
            console.warn('[asset-form] unreadable field definitions', e);
            return [];
        }
    }

    _field(definition) {
        const wrapper = document.createElement('div');
        wrapper.className = 'mb-3';

        const name = `attributes[${definition.key}]`;
        const id = `attributes-${definition.key}`;

        const label = document.createElement('label');
        label.className = 'form-label';
        label.setAttribute('for', id);
        label.textContent = definition.unit
            ? `${definition.label} (${definition.unit})`
            : definition.label;
        wrapper.appendChild(label);
        wrapper.appendChild(this._input(definition, name, id));
        return wrapper;
    }

    _input(definition, name, id) {
        if (definition.type === 'choice') return this._select(definition, name, id);
        if (definition.type === 'boolean') return this._checkbox(definition, name, id);

        const input = document.createElement('input');
        input.id = id;
        input.name = name;
        input.className = 'form-control';
        if (definition.type === 'date') {
            input.type = 'date';
        } else if (definition.type === 'number') {
            input.type = 'text';
            input.inputMode = 'decimal';
        } else {
            input.type = 'text';
        }
        if (definition.required) input.required = true;
        return input;
    }

    _select(definition, name, id) {
        const select = document.createElement('select');
        select.id = id;
        select.name = name;
        select.className = 'form-select';
        if (definition.required) select.required = true;

        const blank = document.createElement('option');
        blank.value = '';
        blank.textContent = 'Choose…';
        select.appendChild(blank);

        (definition.choices || []).forEach((choice) => {
            const option = document.createElement('option');
            option.value = choice;
            option.textContent = choice;
            select.appendChild(option);
        });
        return select;
    }

    _checkbox(definition, name, id) {
        const wrapper = document.createElement('div');
        wrapper.className = 'form-check form-switch';

        // An unchecked box submits nothing, so "not ticked" has to reach the
        // server as an explicit false rather than as an absent key.
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        hidden.value = 'false';
        wrapper.appendChild(hidden);

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.name = name;
        input.value = 'true';
        input.className = 'form-check-input';
        wrapper.appendChild(input);
        return wrapper;
    }
}
